import { useState } from 'react'
import { 
  Globe, 
  Star,
  Circle,
  Zap,
  Shield,
  Eye,
  Flag,
  Coins,
  Gem,
  Ship
} from 'lucide-react'
import { Button } from '../components/ui/button'
import GlassCard from '../components/GlassCard'
import Starfield from '../components/Starfield'
import { useGameState } from '../contexts/GameStateContext'

interface StarSystem {
  id: string
  name: string
  type: 'habitable' | 'hostile' | 'resource' | 'empty' | 'contested'
  planets: number
  owner?: string
  distance: number
  coordinates: { x: number; y: number }
  threat: 'low' | 'medium' | 'high'
  resources: string[]
}

const GalaxyMap = () => {
  const { stats, planets, fleets, addPlanet, updateFleet, spendResources, canAfford } = useGameState()
  const [selectedSystem, setSelectedSystem] = useState<StarSystem | null>(null)
  const [isExploring, setIsExploring] = useState<string | null>(null)
  const [isColonizing, setIsColonizing] = useState<string | null>(null)

  const [systems] = useState<StarSystem[]>([
    {
      id: '1',
      name: 'Sol System',
      type: 'habitable',
      planets: 8,
      owner: 'Terran Federation',
      distance: 0,
      coordinates: { x: 0, y: 0 },
      threat: 'low',
      resources: ['Metal', 'Water', 'Energy']
    },
    {
      id: '2',
      name: 'Alpha Centauri',
      type: 'habitable',
      planets: 3,
      distance: 4.3,
      coordinates: { x: 150, y: 120 },
      threat: 'low',
      resources: ['Rare Metals', 'Crystals']
    },
    {
      id: '3',
      name: 'Kepler-442',
      type: 'resource',
      planets: 5,
      distance: 1200,
      coordinates: { x: 300, y: 200 },
      threat: 'medium',
      resources: ['Quantum Materials', 'Dark Matter']
    },
    {
      id: '4',
      name: 'Wolf 359',
      type: 'hostile',
      planets: 2,
      distance: 7.9,
      coordinates: { x: 80, y: 280 },
      threat: 'high',
      resources: ['Weapons Grade Materials']
    },
    {
      id: '5',
      name: 'Proxima Centauri',
      type: 'contested',
      planets: 1,
      owner: 'Disputed',
      distance: 4.2,
      coordinates: { x: 180, y: 150 },
      threat: 'high',
      resources: ['Strategic Resources']
    }
  ])

  const handleExploreSystem = (system: StarSystem) => {
    const explorationCost = Math.floor(system.distance * 10) // Cost based on distance
    if (!canAfford({ credits: explorationCost, energy: 100 })) return

    setIsExploring(system.id)
    try {
      // Update local state
      spendResources({ credits: explorationCost, energy: 100 })
      
      // Send fleet to explore
      const availableFleet = fleets.find(f => f.status === 'idle')
      if (availableFleet) {
        updateFleet(availableFleet.id, {
          status: 'exploring',
          destination: system.name,
          eta: `${Math.ceil(system.distance / 10)}h`
        })
      }
    } catch (error) {
      console.error('Failed to explore system:', error)
    } finally {
      setIsExploring(null)
    }
  }

  const handleColonizeSystem = (system: StarSystem) => {
    const colonizationCost = 5000 // Fixed colonization cost
    if (!canAfford({ credits: colonizationCost, minerals: 2000, energy: 1000 })) return

    setIsColonizing(system.id)
    try {
      // Create planet data
      const newPlanet = {
        name: `${system.name} Colony`,
        type: system.type as 'habitable' | 'hostile' | 'resource' | 'empty',
        owner: 'player',
        buildings: [
          { id: '1', type: 'mine' as const, level: 1, production: 50, energyCost: 25 }
        ],
        resources: system.resources.map((resource, index) => ({
          type: (resource.toLowerCase().includes('metal') ? 'metal' : 
                 resource.toLowerCase().includes('energy') ? 'energy' : 'rare_minerals') as 'metal' | 'energy' | 'rare_minerals',
          amount: 500,
          productionRate: 50 + (index * 25)
        })),
        population: 100000,
        defenseLevel: 1
      }

      // Update local state
      spendResources({ credits: colonizationCost, minerals: 2000, energy: 1000 })
      addPlanet(newPlanet)
      
      setSelectedSystem(null)
    } catch (error) {
      console.error('Failed to colonize system:', error)
    } finally {
      setIsColonizing(null)
    }
  }

  const getSystemColor = (type: string) => {
    switch (type) {
      case 'habitable': return 'bg-green-500'
      case 'hostile': return 'bg-red-500'
      case 'resource': return 'bg-yellow-500'
      case 'contested': return 'bg-purple-500'
      case 'empty': return 'bg-gray-500'
      default: return 'bg-gray-500'
    }
  }

  const getSystemIcon = (type: string) => {
    switch (type) {
      case 'habitable': return Circle
      case 'hostile': return Zap
      case 'resource': return Star
      case 'contested': return Shield
      case 'empty': return Globe
      default: return Globe
    }
  }

  const getThreatColor = (threat: string) => {
    switch (threat) {
      case 'low': return 'text-green-400'
      case 'medium': return 'text-yellow-400'
      case 'high': return 'text-red-400'
      default: return 'text-gray-400'
    }
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#0a0a0a] via-[#1a1a2e] to-[#16213e] relative overflow-hidden">
      <Starfield density={400} speed={0.3} />
      <div className="relative z-10 pt-16">
        <div className="container mx-auto px-4 py-8">
          {/* Header with Resources */}
          <GlassCard variant="premium" className="mb-8">
            <div className="flex items-center justify-between">
              <div>
                <h1 className="text-4xl font-bold text-white mb-2 flex items-center">
                  <Globe className="w-10 h-10 mr-3 text-purple-400" />
                  Galaxy Map
                </h1>
                <p className="text-purple-300">Explore the known universe and discover new worlds</p>
              </div>
              <div className="flex items-center space-x-6">
                <div className="flex items-center space-x-2">
                  <Coins className="w-5 h-5 text-yellow-400" />
                  <span className="text-white font-bold">{stats.credits.toLocaleString()}</span>
                </div>
                <div className="flex items-center space-x-2">
                  <Gem className="w-5 h-5 text-cyan-400" />
                  <span className="text-white font-bold">{stats.minerals.toLocaleString()}</span>
                </div>
                <div className="flex items-center space-x-2">
                  <Globe className="w-5 h-5 text-green-400" />
                  <span className="text-white font-bold">{stats.planets}</span>
                </div>
              </div>
            </div>
          </GlassCard>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {/* Systems List */}
            <div className="lg:col-span-2">
              <GlassCard variant="default">
                <h2 className="text-xl font-bold text-white mb-4 flex items-center">
                  <Star className="w-6 h-6 mr-2 text-yellow-400" />
                  Known Systems
                </h2>
                
                <div className="space-y-4">
                  {systems.map((system) => {
                    const SystemIcon = getSystemIcon(system.type)
                    const isOwned = planets.some(p => p.name.includes(system.name))
                    const explorationCost = Math.floor(system.distance * 10)
                    const canExplore = canAfford({ credits: explorationCost, energy: 100 })
                    const canColonize = canAfford({ credits: 5000, minerals: 2000, energy: 1000 })
                    
                    return (
                      <div 
                        key={system.id} 
                        className={`p-4 rounded-lg border transition-colors cursor-pointer ${
                          selectedSystem?.id === system.id 
                            ? 'border-purple-500 bg-purple-500/10' 
                            : 'border-white/10 bg-white/5 hover:border-white/20'
                        }`}
                        onClick={() => setSelectedSystem(selectedSystem?.id === system.id ? null : system)}
                      >
                        <div className="flex items-center justify-between">
                          <div className="flex items-center space-x-3">
                            <div className={`w-8 h-8 rounded-full flex items-center justify-center ${getSystemColor(system.type)}`}>
                              <SystemIcon className="w-4 h-4 text-white" />
                            </div>
                            <div>
                              <h3 className="text-lg font-bold text-white">{system.name}</h3>
                              <p className="text-sm text-gray-400">{system.distance} light-years away</p>
                            </div>
                          </div>
                          
                          <div className="flex items-center space-x-4">
                            <div className="text-right">
                              <div className="text-sm text-gray-400">Planets</div>
                              <div className="text-white font-bold">{system.planets}</div>
                            </div>
                            <div className="text-right">
                              <div className="text-sm text-gray-400">Threat</div>
                              <div className={`font-bold capitalize ${getThreatColor(system.threat)}`}>
                                {system.threat}
                              </div>
                            </div>
                            {isOwned && (
                              <div className="flex items-center space-x-1 text-green-400">
                                <Flag className="w-4 h-4" />
                                <span className="text-sm">Owned</span>
                              </div>
                            )}
                          </div>
                        </div>

                        {selectedSystem?.id === system.id && (
                          <div className="mt-4 pt-4 border-t border-white/10">
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                              <div>
                                <h4 className="text-white font-semibold mb-2">Resources:</h4>
                                <div className="flex flex-wrap gap-2">
                                  {system.resources.map((resource, index) => (
                                    <span key={index} className="px-2 py-1 text-xs bg-blue-500/20 text-blue-300 rounded">
                                      {resource}
                                    </span>
                                  ))}
                                </div>
                              </div>
                              
                              <div className="flex flex-col space-y-2">
                                {!isOwned && (
                                  <>
                                    <Button
                                      onClick={(e) => {
                                        e.stopPropagation()
                                        handleExploreSystem(system)
                                      }}
                                      disabled={!canExplore || isExploring === system.id}
                                      className="bg-blue-600 hover:bg-blue-700"
                                      size="sm"
                                    >
                                      <Eye className="w-4 h-4 mr-2" />
                                      {isExploring === system.id ? 'Exploring...' : `Explore (${explorationCost} Credits)`}
                                    </Button>
                                    
                                    {system.type === 'habitable' && (
                                      <Button
                                        onClick={(e) => {
                                          e.stopPropagation()
                                          handleColonizeSystem(system)
                                        }}
                                        disabled={!canColonize || isColonizing === system.id}
                                        className="bg-green-600 hover:bg-green-700"
                                        size="sm"
                                      >
                                        <Flag className="w-4 h-4 mr-2" />
                                        {isColonizing === system.id ? 'Colonizing...' : 'Colonize (5000 Credits)'}
                                      </Button>
                                    )}
                                  </>
                                )}
                              </div>
                            </div>
                          </div>
                        )}
                      </div>
                    )
                  })}
                </div>
              </GlassCard>
            </div>

            {/* Fleet Status */}
            <div>
              <GlassCard variant="default">
                <h3 className="text-lg font-bold text-white mb-4 flex items-center">
                  <Ship className="w-5 h-5 mr-2 text-blue-400" />
                  Fleet Status
                </h3>
                
                <div className="space-y-3">
                  {fleets.map((fleet) => (
                    <div key={fleet.id} className="p-3 bg-white/5 rounded-lg">
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-white font-medium">{fleet.name}</span>
                        <span className={`text-xs px-2 py-1 rounded-full capitalize ${
                          fleet.status === 'idle' ? 'bg-gray-600 text-gray-300' :
                          fleet.status === 'exploring' ? 'bg-blue-600 text-blue-300' :
                          'bg-green-600 text-green-300'
                        }`}>
                          {fleet.status}
                        </span>
                      </div>
                      <div className="text-sm text-gray-400">
                        Location: {fleet.location}
                      </div>
                      {fleet.destination && (
                        <div className="text-sm text-blue-400">
                          → {fleet.destination} {fleet.eta && `(${fleet.eta})`}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </GlassCard>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default GalaxyMap